import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import {
  getProjectConfig,
  updateProjectProfiles,
  parseDefaultBrandCode,
  parseEnumMultiValue,
  parseEnumSingleValue,
  orderProjectFeatureCodes,
  type ProfileItem,
} from '../../utils/projectConfig';
import { CommonUtils } from '../../utils/commonUtils';
import { getBrandListFlat } from '../../utils/brand';
import { ChevronDown, ChevronUp, RotateCcw, Save } from 'lucide-react';

// 프로필 카테고리 (API와 동일)
type ProfileCategory = 'PROJECT' | 'DEFAULT' | 'ETC';

// 공통 옵션 세트 (설정키 반복 시 재사용)
const OPTION_SETS = {
  FEATURE: [
    { value: 'SHOWCASE', label: '메뉴 소개' },
    { value: 'MEMBERSHIP', label: '멤버십' },
    { value: 'MOBILE_ORDER', label: '모바일 오더' },
    { value: 'MALL', label: '쇼핑몰' },
    // { value: 'QR_ORDER', label: 'QR_ORDER' },
    // { value: 'KIOSK', label: 'KIOSK' },
    // { value: 'TABLE_ORDER', label: 'TABLE_ORDER' },
  ],
  EARN_METHOD: [
    { value: 'NONE', label: '없음' },
    { value: 'AGENT', label: '에이전트' },
    { value: 'POS', label: 'POS' },
  ],
  EARN_POLICY: [
    { value: 'DISABLED', label: '비허용' },
    { value: 'ALLOWED', label: '허용' },
  ],
} as const;

// 설정키 값 타입: ENUM_MULTI(다중선택) | ENUM_SINGLE(단일선택) | CODE_REF(셀렉트박스) | STRING(문자열)
export type ConfigKeyType = 'ENUM_MULTI' | 'ENUM_SINGLE' | 'CODE_REF' | 'STRING';

interface SettingKeyDef {
  settingKey: string;
  label: string;
  type: ConfigKeyType;
  optionSetKey?: keyof typeof OPTION_SETS;
  options?: { value: string; label: string }[];
}

interface ProfileDef {
  category: ProfileCategory;
  label: string;
  settingKeys: SettingKeyDef[];
}

const PROFILES: ProfileDef[] = [
  {
    category: 'PROJECT',
    label: '프로젝트',
    settingKeys: [
      { settingKey: 'FEATURE', label: '사용 기능', type: 'ENUM_MULTI', optionSetKey: 'FEATURE' },
      { settingKey: 'EARN_METHOD', label: '적립 방식', type: 'ENUM_SINGLE', optionSetKey: 'EARN_METHOD' },
      {
        settingKey: 'EARN_POLICY',
        label: '미등록 고객 적립(간편 가입)',
        type: 'ENUM_SINGLE',
        optionSetKey: 'EARN_POLICY',
      },
      { settingKey: 'SIGNUP_EXPIRE_DAYS', label: '간편 가입 만료', type: 'STRING' },
    ],
  },
  {
    category: 'DEFAULT',
    label: '기본값',
    settingKeys: [{ settingKey: 'BRAND', label: '기본 브랜드', type: 'CODE_REF', options: [] }],
  },
  {
    category: 'ETC',
    label: '기타',
    settingKeys: [
      { settingKey: 'NOTE', label: 'NOTE', type: 'STRING' },
    ],
  },
];

// 설정키별 저장 상태 (category_key 형태)
type ConfigState = Record<string, string[] | string>;

const getConfigId = (category: ProfileCategory, settingKey: string) => `${category}_${settingKey}`;

const EARN_POLICY_CONFIG_ID = getConfigId('PROJECT', 'EARN_POLICY');
const SIGNUP_EXPIRE_DAYS_CONFIG_ID = getConfigId('PROJECT', 'SIGNUP_EXPIRE_DAYS');
const DEFAULT_SIGNUP_EXPIRE_DAYS = '30';

const emptyState = (): ConfigState => {
  const initial: ConfigState = {};
  PROFILES.forEach((profile) => {
    profile.settingKeys.forEach((sk) => {
      const id = getConfigId(profile.category, sk.settingKey);
      if (sk.type === 'ENUM_MULTI') initial[id] = [];
      else initial[id] = '';
    });
  });
  return initial;
};

// 변경 요약 항목
interface ChangeItem {
  configId: string;
  category: ProfileCategory;
  categoryLabel: string;
  settingKeyLabel: string;
  settingKey: string;
  keyType: ConfigKeyType;
  from: string;
  to: string;
}

const ProjectConfig: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('project-config');

  const [configValues, setConfigValues] = useState<ConfigState>(emptyState);
  const [initialValues, setInitialValues] = useState<ConfigState>(emptyState);
  const [sectionExpanded, setSectionExpanded] = useState<Record<ProfileCategory, boolean>>({
    PROJECT: true,
    DEFAULT: true,
    ETC: true,
  });

  const [saving, setSaving] = useState(false);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [brandOptions, setBrandOptions] = useState<{ value: string; label: string }[]>([]);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // URL의 projectCode 검증 및 현재 프로젝트와 일치 여부
  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  // 전달된 projectCode로 설정 조회 (API 스켈레톤)
  useEffect(() => {
    if (!projectCodeFromUrl || !projectDetail) return;
    const fetchConfig = async () => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const res = await getProjectConfig(projectCodeFromUrl);
        const next: ConfigState = { ...emptyState() };
        res.items.forEach((item) => {
          let val = item.value;
          for (const profile of PROFILES) {
            const def = profile.settingKeys.find((sk) => getConfigId(profile.category, sk.settingKey) === item.configId);
            if (def?.type === 'ENUM_SINGLE' && Array.isArray(val)) {
              val = val.length ? val[0] : '';
              break;
            }
          }
          next[item.configId] = val;
        });
        setConfigValues(next);
        setInitialValues(next);
      } catch (e) {
        setConfigError(e instanceof Error ? e.message : '설정을 불러오지 못했습니다.');
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, [projectCodeFromUrl, projectDetail]);

  // 기본 브랜드 셀렉트용 브랜드 목록 (상품 추가/수정과 동일 API)
  useEffect(() => {
    if (!projectCodeFromUrl) return;
    let cancelled = false;
    setBrandOptionsLoading(true);
    getBrandListFlat(projectCodeFromUrl)
      .then((res) => {
        if (cancelled) return;
        const nbsp = '\u00A0';
        const options = (res?.brands ?? []).map((brand) => {
          const indent = (nbsp + nbsp).repeat(brand.depth);
          const prefix = brand.depth > 0 ? 'ㄴ ' : '';
          return { value: brand.code, label: `${indent}${prefix}${brand.name}` };
        });
        setBrandOptions(options);
      })
      .catch(() => {
        if (!cancelled) setBrandOptions([]);
      })
      .finally(() => {
        if (!cancelled) setBrandOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectCodeFromUrl]);

  // 저장 성공 메시지 3초 후 자동 숨김
  useEffect(() => {
    if (!saveSuccess) return;
    const t = window.setTimeout(() => setSaveSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [saveSuccess]);

  const getOptions = (def: SettingKeyDef) => {
    if (def.optionSetKey && OPTION_SETS[def.optionSetKey]) {
      return OPTION_SETS[def.optionSetKey];
    }
    return def.options ?? [];
  };

  /** 코드값을 라벨로 변환하여 변경 요약 등에 표시 */
  const formatValueForDisplay = (
    sk: SettingKeyDef,
    v: string[] | string,
    opts: { brandOptions: { value: string; label: string }[] }
  ): string => {
    if (sk.type === 'ENUM_MULTI') {
      const arr = (Array.isArray(v) ? v : [v]).filter(Boolean);
      if (!arr.length) return '—';
      const options = getOptions(sk);
      return arr.map((code) => options.find((o) => o.value === code)?.label ?? code).join(', ');
    }
    if (sk.type === 'ENUM_SINGLE') {
      const code = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : '');
      if (!code) return '—';
      const options = getOptions(sk);
      return options.find((o) => o.value === code)?.label ?? code;
    }
    if (sk.type === 'CODE_REF') {
      const code = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : '');
      if (!code) return '—';
      if (sk.settingKey === 'BRAND') {
        return opts.brandOptions.find((o) => o.value === code)?.label ?? code;
      }
      return code;
    }
    // STRING
    if (sk.settingKey === 'SIGNUP_EXPIRE_DAYS') {
      const days = (typeof v === 'string' ? v : '').trim();
      return days ? `${days}일` : '—';
    }
    return (typeof v === 'string' ? v : '') || '—';
  };

  const getChangeSummary = (): ChangeItem[] => {
    const items: ChangeItem[] = [];
    const opts = { brandOptions };
    PROFILES.forEach((profile) => {
      profile.settingKeys.forEach((sk) => {
        const configId = getConfigId(profile.category, sk.settingKey);
        const from = initialValues[configId];
        const to = configValues[configId];
        if (JSON.stringify(from) !== JSON.stringify(to)) {
          items.push({
            configId,
            category: profile.category,
            categoryLabel: profile.label,
            settingKeyLabel: sk.label,
            settingKey: sk.settingKey,
            keyType: sk.type,
            from: formatValueForDisplay(sk, from, opts),
            to: formatValueForDisplay(sk, to, opts),
          });
        }
      });
    });
    return items;
  };

  const changedSummary = getChangeSummary();
  const hasChanges = changedSummary.length > 0;
  const isEarnPolicyAllowed =
    parseEnumSingleValue(configValues[EARN_POLICY_CONFIG_ID] as string[] | string) === 'ALLOWED';

  const handleToggleOption = (configId: string, value: string) => {
    setConfigValues((prev) => {
      const current = (prev[configId] as string[]) ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [configId]: next };
    });
  };

  const handleSingleSelectOption = (configId: string, value: string) => {
    setConfigValues((prev) => {
      const next = { ...prev, [configId]: value };
      if (configId === EARN_POLICY_CONFIG_ID && value === 'ALLOWED') {
        const expire = String(prev[SIGNUP_EXPIRE_DAYS_CONFIG_ID] ?? '').trim();
        if (!expire) {
          next[SIGNUP_EXPIRE_DAYS_CONFIG_ID] = DEFAULT_SIGNUP_EXPIRE_DAYS;
        }
      }
      return next;
    });
  };

  const handleSelectChange = (configId: string, value: string) => {
    if (configId === SIGNUP_EXPIRE_DAYS_CONFIG_ID && !isEarnPolicyAllowed) return;
    setConfigValues((prev) => ({ ...prev, [configId]: value }));
  };

  const handleReset = () => {
    setConfigValues({ ...initialValues });
  };

  const handleSaveAll = async () => {
    if (!projectCodeFromUrl || !hasChanges) return;
    setSaving(true);
    setConfigError(null);
    setSaveSuccess(false);
    try {
      let profiles: ProfileItem[] = changedSummary.map((item) => ({
        category: item.category,
        settingKey: item.settingKey,
        settingValue: {
          type: item.keyType,
          value: configValues[item.configId],
        },
      }));

      const earnPolicyAllowedOnSave =
        parseEnumSingleValue(configValues[EARN_POLICY_CONFIG_ID] as string[] | string) === 'ALLOWED';
      if (!earnPolicyAllowedOnSave) {
        profiles = profiles.filter((p) => p.settingKey !== 'SIGNUP_EXPIRE_DAYS');
      }
      const initialSignupExpire = String(initialValues[SIGNUP_EXPIRE_DAYS_CONFIG_ID] ?? '').trim();
      const savingSignupExpire = profiles.some((p) => p.settingKey === 'SIGNUP_EXPIRE_DAYS');
      if (earnPolicyAllowedOnSave && !initialSignupExpire && !savingSignupExpire) {
        const expireValue =
          String(configValues[SIGNUP_EXPIRE_DAYS_CONFIG_ID] ?? '').trim() || DEFAULT_SIGNUP_EXPIRE_DAYS;
        profiles.push({
          category: 'PROJECT',
          settingKey: 'SIGNUP_EXPIRE_DAYS',
          settingValue: { type: 'STRING', value: expireValue },
        });
      }

      await updateProjectProfiles(projectCodeFromUrl, profiles);

      const savedSignupExpire = profiles.find((p) => p.settingKey === 'SIGNUP_EXPIRE_DAYS');
      const nextValues = { ...configValues };
      if (savedSignupExpire && typeof savedSignupExpire.settingValue.value === 'string') {
        nextValues[SIGNUP_EXPIRE_DAYS_CONFIG_ID] = savedSignupExpire.settingValue.value;
      }
      setConfigValues(nextValues);
      setInitialValues(nextValues);
      CommonUtils.patchSelectedProject(projectCodeFromUrl, {
        featureCodes: orderProjectFeatureCodes(
          parseEnumMultiValue(configValues[getConfigId('PROJECT', 'FEATURE')] as string[] | string)
        ),
        earnMethod: parseEnumSingleValue(
          configValues[getConfigId('PROJECT', 'EARN_METHOD')] as string[] | string
        ),
        defaultBrandCode: parseDefaultBrandCode(
          configValues[getConfigId('DEFAULT', 'BRAND')] as string[] | string
        ),
      });
      setSaveSuccess(true);
    } catch (e) {
      setConfigError(e instanceof Error ? e.message : '변경사항 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (category: ProfileCategory) => {
    setSectionExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="프로젝트 설정"
          subtitle={projectCodeFromUrl ? `${projectDetail.name} (${projectCodeFromUrl})` : '카테고리별 프로젝트 설정을 관리합니다'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            {configError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {configError}
              </div>
            )}
            {saveSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                설정이 저장되었습니다.
              </div>
            )}
            {configLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}

            {!configLoading && (
              <>
                {/* 상단: 변경 요약 + 초기화 / 변경사항 저장 */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        disabled={!hasChanges}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        초기화
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveAll}
                        disabled={!hasChanges || saving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {saving ? (
                          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        변경사항 저장
                      </button>
                    </div>
                    {hasChanges && (
                      <p className="text-sm text-amber-600">
                        저장하지 않은 변경사항이 {changedSummary.length}건 있습니다.
                      </p>
                    )}
                  </div>
                  {changedSummary.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">변경된 항목</p>
                      <ul className="space-y-1.5 text-sm">
                        {changedSummary.map((item) => (
                          <li key={item.configId} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-medium text-gray-700">
                              [{item.categoryLabel}] {item.settingKeyLabel}
                            </span>
                            <span className="text-gray-500">
                              {item.from} → {item.to}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* 섹션별 접기/펼치기 + 항목 수 */}
                {PROFILES.map((profile) => {
                  const isExpanded = sectionExpanded[profile.category];
                  const itemCount = profile.settingKeys.length;
                  return (
                    <div
                      key={profile.category}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => toggleSection(profile.category)}
                        className="w-full flex items-center justify-between px-4 lg:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-lg font-semibold text-gray-900">
                          {profile.label}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-normal text-gray-500">
                            {itemCount}개 항목
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-gray-200 px-4 lg:px-6 py-4 space-y-4">
                          {profile.settingKeys.map((sk) => {
                            const configId = getConfigId(profile.category, sk.settingKey);
                            const options = getOptions(sk);

                            return (
                              <div
                                key={configId}
                                className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0"
                              >
                                <div className="w-52 sm:w-56 flex-shrink-0">
                                  <p className="text-sm font-medium text-gray-700">{sk.label}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{sk.settingKey}</p>
                                </div>
                                <div className="flex-1 flex flex-wrap items-center gap-2 justify-start text-left min-w-0">
                                  {sk.type === 'ENUM_MULTI' && (
                                    <div className="flex flex-wrap gap-2 justify-start">
                                      {options.map((opt) => {
                                        const isOn = ((configValues[configId] as string[]) ?? []).includes(opt.value);
                                        return (
                                          <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleToggleOption(configId, opt.value)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                              isOn
                                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            <span className={`w-2 h-2 rounded-full ${isOn ? 'bg-white' : 'bg-gray-400'}`} />
                                            {opt.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {sk.type === 'ENUM_SINGLE' && (
                                    <div className="flex flex-wrap gap-2 justify-start">
                                      {options.map((opt) => {
                                        const current = configValues[configId];
                                        const selected = typeof current === 'string' ? current === opt.value : (Array.isArray(current) ? current[0] : '') === opt.value;
                                        return (
                                          <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleSingleSelectOption(configId, opt.value)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                              selected
                                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
                                            {opt.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {sk.type === 'STRING' && sk.settingKey === 'SIGNUP_EXPIRE_DAYS' && (
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="number"
                                          min={1}
                                          value={(configValues[configId] as string) ?? DEFAULT_SIGNUP_EXPIRE_DAYS}
                                          onChange={(e) => handleSelectChange(configId, e.target.value)}
                                          disabled={!isEarnPolicyAllowed}
                                          className="block w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-left disabled:bg-gray-100 disabled:text-gray-500"
                                        />
                                        <span className="text-sm text-gray-600">일</span>
                                      </div>
                                      {!isEarnPolicyAllowed && (
                                        <p className="text-xs text-amber-600">
                                          미등록 고객 적립이 허용(ALLOWED)일 때만 설정할 수 있습니다.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {sk.type === 'STRING' && sk.settingKey !== 'SIGNUP_EXPIRE_DAYS' && (
                                    <textarea
                                      value={(configValues[configId] as string) ?? ''}
                                      onChange={(e) => handleSelectChange(configId, e.target.value)}
                                      placeholder="입력하세요"
                                      rows={4}
                                      className="block w-full min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y"
                                    />
                                  )}
                                  {sk.type === 'CODE_REF' && (
                                    <select
                                      value={(configValues[configId] as string) ?? ''}
                                      onChange={(e) => handleSelectChange(configId, e.target.value)}
                                      disabled={brandOptionsLoading}
                                      className="block w-full min-w-[200px] sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-left disabled:bg-gray-100"
                                    >
                                      <option value="">
                                        {brandOptionsLoading ? '브랜드 목록 로딩 중...' : '브랜드를 선택하세요'}
                                      </option>
                                      {brandOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProjectConfig;